import { useChangeSubscriptionPlan } from '@/hooks/actions/use-change-subscription-plan';
import { formatSettingsDate } from '@/pages/settings/components/format-settings-date';
import { Button, Stack } from '@chakra-ui/react';
import type { OrganizationBillingContext, PlanChangeResponseData } from '@fluenceur/types';
import { NoticePanel } from '@fluenceur/ui';
import { buttonRecipe } from '@fluenceur/theme';
import { useTranslation } from 'react-i18next';

/** A provider receipt explains the intention; canonical queries retain the current offer. */
export function PlanChangeNotice({
  result,
  onCheck,
  busy = false,
  disabled = false,
}: {
  result?: PlanChangeResponseData | null;
  onCheck: () => void;
  busy?: boolean;
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  if (!result || result.channel !== 'stripe') return null;
  const terminal = result.kind === 'pending' && ['expired', 'superseded'].includes(result.status);
  const kind =
    result.kind === 'pending' && (result.status === 'expired' || result.status === 'superseded')
      ? result.status
      : result.kind;
  let hostedInvoiceUrl: string | null = null;
  if (result.kind === 'requires_action' && new Date(result.expiresAt).getTime() > Date.now()) {
    try {
      const url = new URL(result.hostedInvoiceUrl);
      if (
        url.protocol === 'https:' &&
        ['invoice.stripe.com', 'pay.stripe.com'].includes(url.hostname) &&
        !url.username &&
        !url.password &&
        !url.port
      )
        hostedInvoiceUrl = result.hostedInvoiceUrl;
    } catch {
      /* An unqualified URL offers only the scoped status read. */
    }
  }
  const variables =
    result.kind === 'scheduled'
      ? {
          currentPlan: t(`pricing.plan.${result.planIdentifier}.name`),
          plan: t(`pricing.plan.${result.targetPlan}.name`),
          date: formatSettingsDate(result.effectiveAt, t('common.locale')),
        }
      : result.kind === 'applied'
        ? { plan: t(`pricing.plan.${result.planIdentifier}.name`) }
        : {};
  const copy = {
    pending: { title: t('planChange.pending.title'), body: t('planChange.pending.body') },
    requires_action: {
      title: t('planChange.requires_action.title'),
      body: t('planChange.requires_action.body'),
    },
    scheduled: {
      title: t('planChange.scheduled.title'),
      body: t('planChange.scheduled.body', variables),
    },
    applied: {
      title: t('planChange.applied.title'),
      body: t('planChange.applied.body', variables),
    },
    expired: { title: t('planChange.expired.title'), body: t('planChange.expired.body') },
    superseded: { title: t('planChange.superseded.title'), body: t('planChange.superseded.body') },
  };
  return (
    <NoticePanel
      tone={result.kind === 'applied' ? 'success' : 'info'}
      title={copy[kind].title}
      actions={
        result.kind === 'requires_action' || (result.kind === 'pending' && !terminal) ? (
          <Stack direction="row" flexWrap="wrap">
            {hostedInvoiceUrl && (
              <Button asChild disabled={busy || disabled}>
                <a href={hostedInvoiceUrl} target="_blank" rel="noopener noreferrer">
                  {t('planChange.requires_action.continue')}
                </a>
              </Button>
            )}
            <Button
              variant="outline"
              css={buttonRecipe.variants?.variant?.secondary}
              onClick={onCheck}
              loading={busy}
              disabled={disabled}
            >
              {t('onboarding.billing.checkCta')}
            </Button>
          </Stack>
        ) : undefined
      }
    >
      {copy[kind].body}
    </NoticePanel>
  );
}

/** Settings reuses the same scoped GET recovery as plan choices and limit upsells. */
export function CurrentPlanChangeNotice({ context }: { context: OrganizationBillingContext }) {
  const change = useChangeSubscriptionPlan({ billingContext: context });
  return (
    <PlanChangeNotice
      result={change.planChangeResult}
      onCheck={() => {
        void change.checkPlanChange();
      }}
      busy={change.isCheckingPlanChange}
      disabled={!context.capabilities.canCommitPaidPlan}
    />
  );
}
